/**
 * AppDialog — Enterprise Design System
 *
 * A standardised, tickets-style modal shell. Encapsulates the look used by the
 * "Raise Return Request" / "New Ticket" dialogs so every modal in the app can
 * share one consistent header, body, and footer treatment.
 *
 * Anatomy:
 *   ┌───────────────────────────────────────────────┐
 *   │  [icon]  Title                            [X]   │  ← branded header bar
 *   │          Subtitle                               │
 *   ├───────────────────────────────────────────────┤
 *   │  children (grey-50 body)                        │  ← DialogContent
 *   ├───────────────────────────────────────────────┤
 *   │  hint                     [Cancel] [Primary]    │  ← footer bar
 *   └───────────────────────────────────────────────┘
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - title: string | ReactNode                — header title
 *  - subtitle: string | ReactNode (optional)  — small caption under the title
 *  - icon: React component (optional)         — MUI icon rendered in the header tile
 *  - iconColor: string (default '#00838f')    — header tile background color
 *  - maxWidth: 'xs'|'sm'|'md'|'lg'|'xl' (default 'sm')
 *  - fullWidth: boolean (default true)
 *  - disableBackdropClose: boolean (default true) — clicking the backdrop won't close
 *  - children: ReactNode                      — dialog body
 *
 *  Footer (choose one approach):
 *  - actions: ReactNode                       — fully custom footer content (overrides the rest)
 *  OR the built-in footer:
 *    - onSubmit: () => void                   — primary action handler
 *    - submitLabel: string (default 'Save')
 *    - submitIcon: ReactNode (optional)       — startIcon for the primary button
 *    - submitDisabled: boolean
 *    - loading: boolean                       — spinner on primary button
 *    - cancelLabel: string (default 'Cancel')
 *    - requiredHint: boolean (default false)  — shows "Fields marked with * are required"
 *    - hideFooter: boolean (default false)    — render no footer at all
 *
 *  - contentSx / paperSx: object              — style overrides
 */

import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import { OutlinedButton, LoadingButton } from '../components/Button';

function AppDialog({
  open,
  onClose,
  title,
  subtitle,
  icon: Icon,
  iconColor = '#00838f',
  maxWidth = 'sm',
  fullWidth = true,
  disableBackdropClose = true,
  children,
  // footer
  actions,
  onSubmit,
  submitLabel = 'Save',
  submitIcon,
  submitDisabled = false,
  loading = false,
  cancelLabel = 'Cancel',
  requiredHint = false,
  hideFooter = false,
  // style overrides
  contentSx,
  paperSx,
}) {
  const theme = useTheme();
  // In this codebase breakpoints are remapped: down('sm') === below 768px (phones).
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleClose = (event, reason) => {
    if (disableBackdropClose && reason === 'backdropClick') return;
    onClose?.();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          // Full-screen sheet on phones (no rounded corners / height cap);
          // standard centered modal on larger screens.
          borderRadius: isMobile ? 0 : 3,
          maxHeight: isMobile ? '100%' : '92vh',
          bgcolor: 'grey.50',
          ...paperSx,
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: { xs: 2, sm: 3 },
          py: 2,
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          {Icon && (
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: iconColor,
                flexShrink: 0,
              }}
            >
              <Icon sx={{ color: 'white', fontSize: 22 }} />
            </Box>
          )}
          <Box>
            <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ bgcolor: 'grey.100' }} aria-label="Close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Body */}
      <DialogContent sx={{ px: { xs: 2, sm: 3 }, py: 2.5, bgcolor: 'grey.50', ...contentSx }}>
        {children}
      </DialogContent>

      {/* Footer */}
      {!hideFooter && (
        <DialogActions
          sx={{
            px: { xs: 2, sm: 3 },
            py: 2,
            bgcolor: 'background.paper',
            borderTop: '1px solid',
            borderColor: 'divider',
            gap: 1.5,
          }}
        >
          {actions ? (
            actions
          ) : (
            <>
              {requiredHint && (
                <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto' }}>
                  Fields marked with{' '}
                  <Box component="span" sx={{ color: 'error.main' }}>
                    *
                  </Box>{' '}
                  are required
                </Typography>
              )}
              <OutlinedButton onClick={onClose}>{cancelLabel}</OutlinedButton>
              {onSubmit && (
                <LoadingButton
                  variant="contained"
                  startIcon={submitIcon}
                  onClick={onSubmit}
                  loading={loading}
                  disabled={submitDisabled}
                  sx={{ px: 3, borderRadius: 2, fontWeight: 600 }}
                >
                  {submitLabel}
                </LoadingButton>
              )}
            </>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
}

export default AppDialog;
